import { useState, useRef, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  Box,
  CircularProgress,
  Typography,
  Button,
  TextField,
} from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useKubernetesObjects } from '@backstage/plugin-kubernetes';

const useStyles = makeStyles(theme => ({
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowX: 'auto',
    maxHeight: '500px',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  formControl: {
    minWidth: 250,
    marginRight: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

// Valor del selector para "todos los pods de un clúster": se leen en paralelo y se ordenan por hora.
const ALL_PODS = '__all__';

interface PodInfo {
  name: string;
  namespace: string;
  cluster: string;
  containers: string[];
}

export const KubernetesPodLogs = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  // Use the Kubernetes hook to get pod data
  const { kubernetesObjects, loading: k8sLoading, error: k8sError } = useKubernetesObjects(entity);

  const [selectedPod, setSelectedPod] = useState<string>('');
  const [selectedContainer, setSelectedContainer] = useState<string>('');
  const [logs, setLogs] = useState<string>('');
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [logsError, setLogsError] = useState<string>('');
  const [tailLinesInput, setTailLinesInput] = useState<string>('500');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const hasLoadedPods = useRef(false);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const allLogLines = useMemo(() => logs.split('\n'), [logs]);
  const filteredLogLines = useMemo(() => {
    if (!normalizedSearchTerm) return allLogLines;
    return allLogLines.filter(line => line.toLowerCase().includes(normalizedSearchTerm));
  }, [allLogLines, normalizedSearchTerm]);
  const displayLogs = useMemo(() => filteredLogLines.join('\n'), [filteredLogLines]);

  // Extract pods from Kubernetes objects
  const pods: PodInfo[] = [];
  if (kubernetesObjects?.items) {
    kubernetesObjects.items.forEach(cluster => {
      const clusterName = cluster.cluster.name;
      cluster.resources?.forEach(resource => {
        if (resource.type === 'pods') {
          resource.resources?.forEach((pod: any) => {
            if (pod.metadata && pod.spec?.containers) {
              const containers = pod.spec.containers.map((c: any) => c.name);
              pods.push({
                name: pod.metadata.name,
                namespace: pod.metadata.namespace,
                cluster: clusterName,
                containers,
              });
            }
          });
        }
      });
    });
  }

  if (pods.length > 0) {
    hasLoadedPods.current = true;
  }

  // Auto-select first pod when data loads
  if (pods.length > 0 && !selectedPod && pods[0].containers.length > 0) {
    setSelectedPod(`${pods[0].cluster}/${pods[0].namespace}/${pods[0].name}`);
    setSelectedContainer(pods[0].containers[0]);
  }

  const fetchPodLogs = async (pod: PodInfo, tailLines: number, timestamps: boolean) => {
    const baseUrl = await discoveryApi.getBaseUrl('kubernetes');
    const queryParams = new URLSearchParams({
      container: selectedContainer,
      tailLines: String(tailLines),
      timestamps: String(timestamps),
    });
    const response = await fetch(
      `${baseUrl}/proxy/api/v1/namespaces/${pod.namespace}/pods/${pod.name}/log?${queryParams.toString()}`,
      { headers: { 'Backstage-Kubernetes-Cluster': pod.cluster } },
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${pod.name}: HTTP ${response.status}: ${errorText || response.statusText}`);
    }
    return response.text();
  };

  const fetchLogs = async () => {
    if (!selectedPod || !selectedContainer) return;

    const parsedTailLines = Number(tailLinesInput.trim());
    if (!Number.isInteger(parsedTailLines) || parsedTailLines <= 0) {
      setLogsError('Tail Lines must be a positive integer.');
      return;
    }

    setLogsLoading(true);
    setLogsError('');

    try {
      const [cluster] = selectedPod.split('/');
      if (selectedPod.endsWith(`/${ALL_PODS}`)) {
        // Cada línea empieza con su timestamp RFC3339 (decimales variables): se ordena por la fecha, no por el texto.
        const targets = pods.filter(p => p.cluster === cluster && p.containers.includes(selectedContainer));
        const results = await Promise.all(
          targets.map(async pod => {
            const text = await fetchPodLogs(pod, parsedTailLines, true);
            return text.split('\n').filter(Boolean).map(line => {
              const space = line.indexOf(' ');
              return `${line.slice(0, space)} [${pod.name}]${line.slice(space)}`;
            });
          }),
        );
        const time = (line: string) => Date.parse(line.slice(0, line.indexOf(' ')));
        setLogs(results.flat().sort((a, b) => time(a) - time(b)).join('\n') || 'No logs available');
      } else {
        const pod = pods.find(p => `${p.cluster}/${p.namespace}/${p.name}` === selectedPod);
        if (!pod) throw new Error('el pod ya no existe (se reinició); elige otro');
        const logsText = await fetchPodLogs(pod, parsedTailLines, false);
        setLogs(logsText || 'No logs available');
      }
    } catch (err: any) {
      setLogsError(`Error fetching logs: ${err.message}`);
      setLogs('');
    } finally {
      setLogsLoading(false);
    }
  };

  const kubernetesAnnotation = entity.metadata.annotations?.['backstage.io/kubernetes-id'];

  if (!kubernetesAnnotation) {
    return (
      <Card>
        <CardHeader title="Pod Logs" />
        <CardContent>
          <Typography>
            No Kubernetes configuration found. Add the annotation{' '}
            <code>backstage.io/kubernetes-id</code> to your entity.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (k8sError) {
    return (
      <Card>
        <CardHeader title="Pod Logs" />
        <CardContent>
          <Typography className={classes.errorText}>
            Error loading Kubernetes data: {k8sError}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const clusters = [...new Set(pods.map(p => p.cluster))];
  const currentPod = selectedPod.endsWith(`/${ALL_PODS}`)
    ? pods.find(p => p.cluster === selectedPod.split('/')[0])
    : pods.find(p => `${p.cluster}/${p.namespace}/${p.name}` === selectedPod);

  return (
    <Card>
      <CardHeader title="Pod Logs" />
      <CardContent>
        <Box className={classes.controls}>
          <FormControl className={classes.formControl} variant="outlined" size="small">
            <InputLabel>Pod</InputLabel>
            <Select
              value={selectedPod}
              onChange={e => {
                const newPod = e.target.value as string;
                setSelectedPod(newPod);
                const pod = newPod.endsWith(`/${ALL_PODS}`)
                  ? pods.find(p => p.cluster === newPod.split('/')[0])
                  : pods.find(p => `${p.cluster}/${p.namespace}/${p.name}` === newPod);
                if (pod && pod.containers.length > 0) {
                  setSelectedContainer(pod.containers[0]);
                }
                setLogs('');
                setLogsError('');
              }}
              disabled={k8sLoading || pods.length === 0}
              label="Pod"
            >
              {clusters.map(cluster => (
                <MenuItem key={`${cluster}/${ALL_PODS}`} value={`${cluster}/${ALL_PODS}`}>
                  Todos los pods ({cluster})
                </MenuItem>
              ))}
              {pods.map(pod => (
                <MenuItem
                  key={`${pod.cluster}/${pod.namespace}/${pod.name}`}
                  value={`${pod.cluster}/${pod.namespace}/${pod.name}`}
                >
                  {pod.name} ({pod.cluster})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {currentPod && currentPod.containers.length > 0 && (
            <FormControl className={classes.formControl} variant="outlined" size="small">
              <InputLabel>Container</InputLabel>
              <Select
                value={selectedContainer}
                onChange={e => {
                  setSelectedContainer(e.target.value as string);
                  setLogs('');
                  setLogsError('');
                }}
                disabled={logsLoading}
                label="Container"
              >
                {currentPod.containers.map(container => (
                  <MenuItem key={container} value={container}>
                    {container}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchLogs()}
            disabled={logsLoading || !selectedPod || !selectedContainer}
            size="small"
          >
            {logs ? 'Refresh' : 'Fetch Logs'}
          </Button>

          <TextField
            label="Tail Lines"
            type="number"
            size="small"
            variant="outlined"
            value={tailLinesInput}
            onChange={e => setTailLinesInput(e.target.value)}
            disabled={logsLoading}
            inputProps={{ min: 1, step: 1 }}
            style={{ width: 130 }}
          />

          <TextField
            label="Filter word"
            type="text"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            disabled={logsLoading || !logs}
            placeholder="error"
            style={{ minWidth: 220 }}
          />
        </Box>

        {!logsLoading && !logsError && logs && (
          <Typography variant="caption" color="textSecondary">
            Showing {filteredLogLines.length} of {allLogLines.length} lines
          </Typography>
        )}

        {((k8sLoading && !hasLoadedPods.current) || logsLoading) && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography style={{ marginLeft: 16 }}>
              {k8sLoading ? 'Loading pods...' : 'Loading logs...'}
            </Typography>
          </Box>
        )}

        {logsError && (
          <Typography className={classes.errorText}>{logsError}</Typography>
        )}

        {!logsLoading && !logsError && logs && filteredLogLines.length > 0 && (
          <Box className={classes.logContainer}>{displayLogs}</Box>
        )}

        {!logsLoading && !logsError && logs && filteredLogLines.length === 0 && normalizedSearchTerm && (
          <Typography color="textSecondary">
            No matching lines for "{searchTerm.trim()}"
          </Typography>
        )}

        {!logsLoading && !logsError && !logs && selectedPod && selectedContainer && (
          <Typography color="textSecondary">
            Select a pod and click "Fetch Logs" to view logs
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
